import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardActionArea,
  Typography,
  Link
} from '@mui/material';
import { API_BASE_URL } from '../utils/api';

interface SidebarAdProps {
  categoryId?: number;
}

interface Promotion {
  id: number;
  title: string;
  content: string;
  imageUrl?: string;
  linkUrl?: string;
  type: string;
  displayLocation: string;
  isActive: boolean;
}

const SidebarAd: React.FC<SidebarAdProps> = ({ categoryId }) => {
  const [ads, setAds] = useState<Promotion[]>([]);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const url = categoryId 
          ? `${API_BASE_URL}/api/promotions?type=category_ad&categoryId=${categoryId}&isActive=true`
          : `${API_BASE_URL}/api/promotions?type=sidebar_ad&isActive=true`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          // Filter for sidebar ads
          const sidebarAds = Array.isArray(data) 
            ? data.filter((ad: Promotion) => 
                ad.displayLocation === 'sidebar' || ad.type === 'sidebar_ad'
              )
            : [];
          setAds(sidebarAds.slice(0, 1)); // Show only first ad
        }
      } catch (error) {
        console.error('Error fetching sidebar ads:', error);
      }
    };

    fetchAds();
  }, [categoryId]);

  if (ads.length === 0) {
    return null;
  }

  const ad = ads[0];

  return (
    <Box sx={{ mb: 3 }}>
      <Card 
        sx={{ 
          maxWidth: '100%',
          border: '1px solid #e0e0e0',
          '&:hover': {
            boxShadow: 4
          }
        }}
      >
        <CardActionArea
          component={ad.linkUrl ? Link : 'div'}
          href={ad.linkUrl || undefined}
          target={ad.linkUrl ? '_blank' : undefined}
          rel={ad.linkUrl ? 'noopener noreferrer' : undefined}
          onClick={async () => {
            if (ad.id) {
              try {
                await fetch(`${API_BASE_URL}/api/promotions/${ad.id}/click`, {
                  method: 'POST'
                });
              } catch (error) {
                console.error('Error tracking ad click:', error);
              }
            }
          }}
        >
          {ad.imageUrl && (
            <CardMedia
              component="img"
              height="200"
              image={ad.imageUrl}
              alt={ad.title}
              sx={{ objectFit: 'cover' }}
            />
          )}
          <Box sx={{ p: 2 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                mb: 1, 
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.7rem'
              }}
            >
              Advertisement
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {ad.title}
            </Typography>
            {ad.content && (
              <Typography variant="body2" color="text.secondary">
                {ad.content.length > 100 ? `${ad.content.substring(0, 100)}...` : ad.content}
              </Typography>
            )}
          </Box>
        </CardActionArea>
      </Card>
    </Box>
  );
};

export default SidebarAd;




